import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Grid,
  Divider,
  Tabs,
  Tab,
  Box,
  InputAdornment
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { updateStockAndPrice } from './../../services/ProductService';
import { currencySymbol } from './../../utils/currency';

const EditStockPriceModal = ({ open, onClose, onSave, product, locationId, canEditPrice = true, canEditCost = false }) => {

  const normalizeNumber = (value) => value?.toString().replace(',', '.');
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState({
    price: '',
    priceLocal: '',
    priceWeb: '',
    pricePedidosYa: '',
    cost: '',
    stock: '',
    minimumStock: '',
    suggestedStock: '',
    visibleInLocation: false,
    note: ''
  });

  // DEFINED y RETAIL tienen stock, UNDEFINED no
  const hasStock = product?.productType === 'DEFINED' || product?.productType === 'RETAIL';

  // Solo se muestran las tabs que apliquen según los permisos/tipo de producto.
  const tabs = useMemo(() => {
    const available = [];
    if (canEditPrice) available.push({ key: 'price', label: t('price') || 'Precio' });
    if (canEditCost) available.push({ key: 'cost', label: t('cost') || 'Costo' });
    if (hasStock) available.push({ key: 'stock', label: t('stock') || 'Stock' });
    return available;
  }, [canEditPrice, canEditCost, hasStock, t]);

  useEffect(() => {
    if (open) setActiveTab(0);
  }, [open, product?.id]);

  useEffect(() => {
    if (product) {
      setFormData({
        price: product.price ?? '',
        priceLocal: product.priceLocal ?? '',
        priceWeb: product.priceWeb ?? '',
        pricePedidosYa: product.pricePedidosYa ?? '',
        cost: product.cost ?? '',
        stock: product.stock ?? '',
        minimumStock: product.minimumStock ?? '',
        suggestedStock: product.suggestedStock ?? '',
        visibleInLocation: product.visibleInLocation ?? false,
        note: '',
      });
    }
  }, [product]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    const priceValue = normalizeNumber(formData.price);
    const costValue = normalizeNumber(formData.cost);
    const stockValue = normalizeNumber(formData.stock);
    const minStockValue = normalizeNumber(formData.minimumStock);
    const suggestedStockValue = normalizeNumber(formData.suggestedStock);

    if (canEditPrice && (!priceValue || isNaN(parseFloat(priceValue)) || parseFloat(priceValue) <= 0)) {
      errors.price = t('price_required');
      isValid = false;
    }

    if (canEditPrice) {
      [['priceLocal', 'price_local'], ['priceWeb', 'price_web'], ['pricePedidosYa', 'price_pedidos_ya']].forEach(([field, errorKey]) => {
        const raw = formData[field];
        if (raw === '' || raw === null || raw === undefined) return;
        const value = normalizeNumber(raw);
        if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          errors[field] = t(`${errorKey}_invalid`) || 'Precio inválido';
          isValid = false;
        }
      });
    }

    if (canEditCost && (costValue === '' || costValue === null || costValue === undefined || isNaN(parseFloat(costValue)) || parseFloat(costValue) < 0)) {
      errors.cost = t('cost_required') || t('cost');
      isValid = false;
    }

    if (hasStock) {
      // Un stock ya negativo (por ventas) no debe bloquear editar otros campos: solo se rechaza si cambia.
      const stockUnchanged = parseFloat(stockValue) === parseFloat(product?.stock ?? 0);
      if (!stockValue || isNaN(parseFloat(stockValue)) || (parseFloat(stockValue) < 0 && !stockUnchanged)) {
        errors.stock = t('stock_required');
        isValid = false;
      }

      if (minStockValue !== '' && (!minStockValue || isNaN(parseFloat(minStockValue)) || parseFloat(minStockValue) < 0)) {
        errors.minimumStock = t('min_stock_invalid');
        isValid = false;
      }

      if (suggestedStockValue !== '' && (!suggestedStockValue || isNaN(parseFloat(suggestedStockValue)) || parseFloat(suggestedStockValue) < 0)) {
        errors.suggestedStock = t('suggested_stock_invalid');
        isValid = false;
      }
    }

    // El motivo solo es obligatorio si la cantidad de stock realmente cambia -- editar
    // únicamente precio/costo no debería exigirlo.
    const stockChanged = hasStock && stockValue !== '' && parseFloat(stockValue) !== parseFloat(product?.stock ?? 0);
    if (stockChanged && (!formData.note || !formData.note.trim())) {
      errors.note = t('stock_edit_note_required') || 'Ingresá un motivo para este cambio';
      isValid = false;
    }

    setFormErrors(errors);

    // Si el error queda en una tab que no es la activa, saltar ahí para que se vea.
    const priceFields = ['price', 'priceLocal', 'priceWeb', 'pricePedidosYa'];
    const stockFields = ['stock', 'minimumStock', 'suggestedStock'];
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const tabKey = priceFields.includes(firstErrorField) ? 'price' : stockFields.includes(firstErrorField) ? 'stock' : firstErrorField === 'cost' ? 'cost' : null;
      if (tabKey) {
        const tabIndex = tabs.findIndex((tab) => tab.key === tabKey);
        if (tabIndex !== -1) setActiveTab(tabIndex);
      }
    }

    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      // Precio por canal: vacío = borrar el override (vuelve a usar el precio base); con valor = fijarlo.
      const buildChannelPriceFields = (field, backendField) => {
        const raw = formData[field];
        if (raw === '' || raw === null || raw === undefined) {
          return { [backendField]: null, [`clear_${backendField}`]: true };
        }
        return { [backendField]: parseFloat(normalizeNumber(raw)) };
      };

      const request = {
        location_id: parseInt(locationId),
        price: canEditPrice ? parseFloat(normalizeNumber(formData.price)) : product?.price ?? 0,
        ...(canEditPrice
          ? {
              ...buildChannelPriceFields('priceLocal', 'price_local'),
              ...buildChannelPriceFields('priceWeb', 'price_web'),
              ...buildChannelPriceFields('pricePedidosYa', 'price_pedidos_ya'),
            }
          : {}),
        ...(canEditCost
          ? {
              cost: parseFloat(normalizeNumber(formData.cost)),
            }
          : {}),
        stock: hasStock ? parseFloat(normalizeNumber(formData.stock)) : 0,
        minimum_stock: hasStock
          ? formData.minimumStock !== '' ? parseFloat(normalizeNumber(formData.minimumStock)) : 0
          : 0,
        suggested_stock: hasStock
          ? formData.suggestedStock !== '' ? parseFloat(normalizeNumber(formData.suggestedStock)) : 0
          : 0,
        visible_in_location: !!formData.visibleInLocation,
        note: formData.note.trim(),
      };

      const response = await updateStockAndPrice(product.id, request);
      onSave(response);
      onClose();
    } catch (error) {
      console.error('Error updating product:', error);
      alert(t('error_updating_product'));
    } finally {
      setLoading(false);
    }
  };

  // Los montos se editan en la moneda en que el producto los tiene guardados (precio y costo pueden estar en monedas distintas).
  const priceAdornment = { startAdornment: <InputAdornment position="start">{currencySymbol(product?.priceCurrency)}</InputAdornment> };
  const costAdornment = { startAdornment: <InputAdornment position="start">{currencySymbol(product?.costCurrency)}</InputAdornment> };

  const priceTabIndex = tabs.findIndex((tab) => tab.key === 'price');
  const costTabIndex = tabs.findIndex((tab) => tab.key === 'cost');
  const stockTabIndex = tabs.findIndex((tab) => tab.key === 'stock');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{canEditPrice || canEditCost ? t('edit_stock_price') : t('edit_stock')}</DialogTitle>
      <DialogContent>
        <p>{product ? t((canEditPrice || canEditCost) ? 'edit_stock_price_description' : 'edit_stock_description', { name: product.name }) : ''}</p>

        {tabs.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        )}

        {canEditPrice && (
          <Box hidden={activeTab !== priceTabIndex}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('base_price')}
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  InputProps={priceAdornment}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.price}
                  helperText={formErrors.price}
                />
              </Grid>
              <Grid item xs={12}>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                  {t('channel_price_description') || 'Precio propio por canal (opcional). Si se deja vacío, se usa el precio base.'}
                </p>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('price_local') || 'Precio Local'}
                  type="number"
                  value={formData.priceLocal}
                  onChange={(e) => handleChange('priceLocal', e.target.value)}
                  InputProps={priceAdornment}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.priceLocal}
                  helperText={formErrors.priceLocal}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('price_web') || 'Precio Web'}
                  type="number"
                  value={formData.priceWeb}
                  onChange={(e) => handleChange('priceWeb', e.target.value)}
                  InputProps={priceAdornment}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.priceWeb}
                  helperText={formErrors.priceWeb}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('price_pedidos_ya') || 'Precio Pedidos YA'}
                  type="number"
                  value={formData.pricePedidosYa}
                  onChange={(e) => handleChange('pricePedidosYa', e.target.value)}
                  InputProps={priceAdornment}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.pricePedidosYa}
                  helperText={formErrors.pricePedidosYa}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {canEditCost && (
          <Box hidden={activeTab !== costTabIndex}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('cost')}
                  type="number"
                  value={formData.cost}
                  onChange={(e) => handleChange('cost', e.target.value)}
                  InputProps={costAdornment}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.cost}
                  helperText={formErrors.cost || (t('manual_cost_help') || 'Este costo se usará hasta que un recálculo posterior lo actualice.')}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {hasStock && (
          <Box hidden={activeTab !== stockTabIndex}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('stock')}
                  type="number"
                  value={formData.stock}
                  onChange={(e) => handleChange('stock', e.target.value)}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.stock}
                  helperText={formErrors.stock}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('minimum_stock')}
                  type="number"
                  value={formData.minimumStock}
                  onChange={(e) => handleChange('minimumStock', e.target.value)}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.minimumStock}
                  helperText={formErrors.minimumStock}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('suggested_stock')}
                  type="number"
                  value={formData.suggestedStock}
                  onChange={(e) => handleChange('suggestedStock', e.target.value)}
                  fullWidth
                  margin="dense"
                  error={!!formErrors.suggestedStock}
                  helperText={formErrors.suggestedStock}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <TextField
              label={t('stock_edit_note') || 'Motivo del cambio'}
              placeholder={t('stock_edit_note_placeholder') || 'Ej: corrección tras conteo físico'}
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              error={!!formErrors.note}
              helperText={formErrors.note}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton
          loading={loading}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditStockPriceModal;
